import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import messageService from '../../services/messageService';
import {
  sessionFromStatus, isUnlocked, msUntilLock, vaultEntryThread, firstRegularThread, wrongCodeError, DEFAULT_WINDOW_MIN,
} from './secureModel';
import {
  biometricSupport, biometricLabel, isEnrolled, markEnrolled, enrolBiometric, forgetBiometric, refreshBiometricCode, biometricUnlock as deviceUnlock, isNative,
  offerDeclined, declineOffer,
} from './biometric';

/**
 * Secure chats (Oct 2026): the state machine behind the padlock in the inbox header.
 * Handoff: ~/Downloads/design_handoff_secure_messages/README.md.
 *
 * Owns the lock status (read from `/messages/secure/lock`; the server keeps the hash,
 * the attempt counter and the authoritative unlock time), which view the inbox is in
 * (`all` | `secure`), the sheet (`modal`), the thing to do once the sheet succeeds
 * (`pending`) and the 1 s tick that locks the vault when the window runs out.
 *
 * The page keeps the threads and the open conversation; it hands in `selectThread`
 * so the vault can land on a thread, `showToast` for the pills, and `refreshThreads`
 * because a shut lock withholds previews that an unlock should bring back.
 */
export default function useSecureChats({ threads, activeId, selectThread, showToast, refreshThreads, userId = null, isMobile = false }) {
  const [session, setSession] = useState(() => sessionFromStatus(null));
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState('all');
  // { key, kind: 'setup' | 'unlock', step, type, change, lockedUntil, pending }
  const [modal, setModal] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const modalKey = useRef(0);
  // Biometrics on THIS device: whether it has a face to offer, what to call it, and
  // whether this account enrolled here. `here` is this device's own record; on the web a
  // passkey made elsewhere may have reached it through the platform's sync, in which
  // case the server's count for this site makes it `enrolled` too — offered on the
  // sheet, but not asked for on its own until it has answered once (`auto`).
  const [support, setSupport] = useState({ supported: false, kind: null, label: 'BIOMETRICS' });
  const [here, setHere] = useState(() => isEnrolled(userId));
  useEffect(() => {
    let cancelled = false;
    setHere(isEnrolled(userId));
    biometricSupport().then((r) => {
      if (cancelled) return;
      setSupport({ supported: r.available, kind: r.kind, label: biometricLabel(r.kind) });
    });
    return () => { cancelled = true; };
  }, [userId]);
  const synced = !isNative() && (session.passkeysHere || 0) > 0;
  const bio = useMemo(() => ({
    ...support,
    enrolled: support.supported && (here || synced),
    auto: support.supported && here,
  }), [support, here, synced]);


  const latest = useRef({ threads, activeId, selectThread, showToast, refreshThreads, session, view, isMobile });
  latest.current = { threads, activeId, selectThread, showToast, refreshThreads, session, view, isMobile };

  // The session as of the last server answer, readable the moment it lands — a render
  // may not have happened yet when the next step asks "is the vault open now?", and a
  // stale no would open a second sheet (and a second Face ID prompt).
  const sessionRef = useRef(session);
  const applyStatus = useCallback((status) => {
    const next = sessionFromStatus(status);
    // The count of passkeys on this site comes with the GET only; later answers keep it.
    if (next.passkeysHere == null) next.passkeysHere = sessionRef.current.passkeysHere ?? 0;
    sessionRef.current = next;
    setSession(next);
    return next;
  }, []);
  const isOpenNow = useCallback(() => {
    const s = sessionRef.current;
    return isUnlocked(s.unlockedAt, s.windowMinutes);
  }, []);

  useEffect(() => {
    let cancelled = false;
    messageService.getSecureLock().then((r) => {
      if (cancelled) return;
      if (r.success) applyStatus(r.data);
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [applyStatus]);

  const unlocked = isUnlocked(session.unlockedAt, session.windowMinutes, now);
  const msLeft = msUntilLock(session.unlockedAt, session.windowMinutes, now);
  const secureCount = useMemo(() => threads.filter((t) => t.isSecure).length, [threads]);
  const secureUnread = useMemo(() => threads.some((t) => t.isSecure && (t.unreadCount || 0) > 0), [threads]);

  // ── Moving between views ──

  const enterVault = useCallback((pending) => {
    const { threads: ts, activeId: cur, selectThread: select, isMobile: phone } = latest.current;
    setView('secure');
    setModal(null);
    // On a phone the vault opens on its list — a thread is a screen of its own there, and
    // only a notification tap earns going straight into one. Desktop's split pane fills
    // the empty side with the thread asked for, else the open secure one, else the first.
    if (phone) {
      const wanted = pending?.threadId && ts.some((t) => t.id === pending.threadId && t.isSecure) ? pending.threadId : null;
      if (wanted && wanted !== cur) select(wanted);
      else if (!wanted && cur && !ts.some((t) => t.id === cur && t.isSecure)) select(null);
      return;
    }
    const id = vaultEntryThread(ts, cur, pending?.threadId || null);
    if (id && id !== cur) select(id);
    else if (!id && ts.some((t) => t.id === cur && !t.isSecure)) select(null);
  }, []);

  const leaveVault = useCallback(() => {
    const { threads: ts, activeId: cur, selectThread: select } = latest.current;
    setView('all');
    if (ts.some((t) => t.id === cur && t.isSecure)) select(firstRegularThread(ts));
  }, []);

  const openModal = useCallback((m) => {
    modalKey.current += 1;
    setModal({ key: modalKey.current, step: 'enter', ...m });
  }, []);

  /**
   * The lock icon from threads view, a notification tap, or a 423 from the API.
   * No lock yet → the secure view opens on its empty state; secure threads with no lock
   * (after FORGOT) → setup first; unlocked → straight in; otherwise the sheet.
   */
  const openVault = useCallback((pending = null) => {
    const { threads: ts } = latest.current;
    const s = sessionRef.current;
    if (!s.hasLock) {
      if (ts.some((t) => t.isSecure) || pending?.threadId) openModal({ kind: 'setup', type: 'pin', change: false, pending: { action: 'reset', threadId: pending?.threadId || null } });
      else setView('secure');
      return;
    }
    if (isUnlocked(s.unlockedAt, s.windowMinutes)) { enterVault(pending); return; }
    openModal({ kind: 'unlock', type: s.type, lockedUntil: s.lockedUntil, pending });
  }, [enterVault, openModal]);

  const toggleVault = useCallback(() => {
    if (latest.current.view === 'secure') leaveVault();
    else openVault(null);
  }, [leaveVault, openVault]);

  const lock = useCallback((auto) => {
    const { threads: ts, activeId: cur, selectThread: select, showToast: toast, refreshThreads: refresh } = latest.current;
    sessionRef.current = { ...sessionRef.current, unlockedAt: null };
    setSession((s) => ({ ...s, unlockedAt: null }));
    setView('all');
    setModal(null);
    setOffer(null);
    if (ts.some((t) => t.id === cur && t.isSecure)) select(firstRegularThread(ts));
    toast(auto ? 'SECURE CHATS LOCKED' : 'LOCKED');
    messageService.secureLockNow().then(() => refresh());
  }, []);
  const lockNow = useCallback(() => lock(false), [lock]);

  // The clock: a second at a time while the window runs, so LOCKS IN counts down and
  // the vault shuts itself on time whichever view is showing.
  useEffect(() => {
    if (!session.unlockedAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session.unlockedAt]);
  useEffect(() => {
    if (!session.unlockedAt || unlocked) return;
    if (view === 'secure') lock(true);
    else { sessionRef.current = { ...sessionRef.current, unlockedAt: null }; setSession((s) => ({ ...s, unlockedAt: null })); }
  }, [unlocked, session.unlockedAt, view, lock]);

  // ── Filing threads ──

  const applySecure = useCallback(async (id, secure) => {
    const { threads: ts, activeId: cur, selectThread: select, showToast: toast, refreshThreads: refresh, view: v } = latest.current;
    const r = await messageService.setThreadSecure(id, secure);
    if (!r.success) { toast((r.error || 'THAT DIDN’T WORK').toUpperCase()); return false; }
    if (secure && v === 'all' && cur === id) select(firstRegularThread(ts.filter((t) => t.id !== id)));
    if (!secure && v === 'secure' && cur === id) select(ts.find((t) => t.isSecure && t.id !== id)?.id || null);
    toast(secure ? 'MOVED TO SECURE CHATS' : 'MOVED BACK TO THREADS');
    await refresh();
    return true;
  }, []);

  const moveToSecure = useCallback((id) => {
    const s = sessionRef.current;
    if (!s.hasLock) { openModal({ kind: 'setup', type: 'pin', change: false, pending: { action: 'secure', id } }); return; }
    applySecure(id, true);
  }, [applySecure, openModal]);
  const removeFromSecure = useCallback((id) => applySecure(id, false), [applySecure]);

  /**
   * CHANGE LOCK (or SET A LOCK with none). The server only takes a new code while the
   * vault is open, so a shut lock asks for the old code first and carries on from there.
   */
  const changeLock = useCallback(() => {
    const s = sessionRef.current;
    if (!s.hasLock) { openModal({ kind: 'setup', type: 'pin', change: false, pending: { action: 'set' } }); return; }
    if (!isUnlocked(s.unlockedAt, s.windowMinutes)) { openModal({ kind: 'unlock', type: s.type, lockedUntil: s.lockedUntil, pending: { action: 'change' } }); return; }
    openModal({ kind: 'setup', type: s.type || 'pin', change: true, pending: { action: 'change' } });
  }, [openModal]);

  const setWindow = useCallback(async (minutes) => {
    const r = await messageService.setSecureWindow(minutes);
    if (r.success) applyStatus(r.data);
    return r.success;
  }, [applyStatus]);

  // ── The sheet's answers ──

  const bioRef = useRef(bio);
  bioRef.current = bio;

  // "Use Face ID next time?" — once, right after the code opened the vault, on a device
  // that has a face and hasn't been asked before. Holds the code so native can keep it.
  const [offer, setOffer] = useState(null);   // { code }
  const maybeOffer = useCallback((code) => {
    const b = bioRef.current;
    if (!b.supported || b.enrolled || offerDeclined(userId)) return;
    setOffer({ code });
  }, [userId]);
  const acceptOffer = useCallback(async () => {
    const o = offer;
    setOffer(null);
    if (!o) return;
    const r = await enrolBiometric({ userId, code: o.code });
    setHere(r.ok);
    if (r.ok || !r.cancelled) latest.current.showToast(r.ok ? `${bioRef.current.label} ON` : r.error || 'THAT DIDN’T WORK');
  }, [offer, userId]);
  const dismissOffer = useCallback(() => {
    setOffer(null);
    declineOffer(userId);
  }, [userId]);

  const onSetup = useCallback(async (type, code) => {
    const r = await messageService.setSecureLock(type, code);
    if (!r.success) return { ok: false, error: (r.error || 'THAT DIDN’T WORK').toUpperCase() };
    applyStatus(r.data);
    const pending = modal?.pending;
    setModal(null);
    await refreshBiometricCode({ userId, code });
    if (pending?.action === 'secure') await applySecure(pending.id, true);
    else if (pending?.action === 'reset') { await latest.current.refreshThreads(); enterVault(pending.threadId ? { threadId: pending.threadId } : null); }
    else if (pending?.action === 'change') latest.current.showToast('LOCK UPDATED');
    else if (pending?.action === 'set') latest.current.showToast('LOCK SET');
    if (pending?.action !== 'change') maybeOffer(code);
    return { ok: true };
  }, [modal, userId, applyStatus, applySecure, enterVault, maybeOffer]);

  /** The vault is open on the server: do what the sheet was opened for. */
  const afterUnlock = useCallback(async (s, pending, code = null) => {
    if (pending?.action === 'change') {
      // Unlocked only to change the code: straight on to setup, not into the vault.
      openModal({ kind: 'setup', type: s.type || 'pin', change: true, pending });
      return;
    }
    if (pending?.action === 'enroll') {
      setModal(null);
      const r = await enrolBiometric({ userId, code });
      setHere(r.ok);
      if (r.ok || !r.cancelled) latest.current.showToast(r.ok ? `${bioRef.current.label} ON` : r.error || 'THAT DIDN’T WORK');
      return;
    }
    await latest.current.refreshThreads();
    enterVault(pending);
    if (code) maybeOffer(code);
  }, [userId, enterVault, openModal, maybeOffer]);

  const onUnlock = useCallback(async (code) => {
    const r = await messageService.secureUnlock(code);
    if (!r.success) return { ok: false, error: (r.error || 'THAT DIDN’T WORK').toUpperCase() };
    const { ok, triesLeft, status } = r.data;
    const s = applyStatus(status);
    if (!ok) return { ok: false, error: wrongCodeError(s.type, triesLeft), lockedUntil: s.lockedUntil, triesLeft };
    await afterUnlock(s, modal?.pending, code);
    return { ok: true };
  }, [modal, applyStatus, afterUnlock]);

  /**
   * The device's face instead of the code. Native hands back the stored code and goes
   * through onUnlock; the web's passkey assertion already opened the vault server-side.
   */
  const onBiometric = useCallback(async () => {
    const b = bioRef.current;
    const r = await deviceUnlock({ userId, label: b.label });
    if (!r.ok) return r;
    if (r.code) return onUnlock(r.code);
    const { ok, triesLeft, lockedUntil, status } = r.result;
    const s = applyStatus(status);
    if (!ok) return { ok: false, error: lockedUntil ? '' : wrongCodeError(s.type, triesLeft), lockedUntil: s.lockedUntil, triesLeft };
    markEnrolled(userId);
    setHere(true);
    await afterUnlock(s, modal?.pending, null);
    return { ok: true };
  }, [userId, modal, onUnlock, applyStatus, afterUnlock]);

  /** Settings: UNLOCK WITH {FACE} on. Native needs the code; the web needs the vault open. */
  const enableBiometric = useCallback(async () => {
    const s = sessionRef.current;
    if (!s.hasLock) { latest.current.showToast('SET A LOCK FIRST'); return; }
    if (isNative() || !isUnlocked(s.unlockedAt, s.windowMinutes)) {
      openModal({ kind: 'unlock', type: s.type, lockedUntil: s.lockedUntil, confirm: true, pending: { action: 'enroll' } });
      return;
    }
    const r = await enrolBiometric({ userId });
    setHere(r.ok);
    if (r.ok || !r.cancelled) latest.current.showToast(r.ok ? `${bioRef.current.label} ON` : r.error || 'THAT DIDN’T WORK');
  }, [userId, openModal]);

  const disableBiometric = useCallback(async () => {
    await forgetBiometric({ userId });
    setHere(false);
    latest.current.showToast(`${bioRef.current.label} OFF`);
  }, [userId]);

  const onForgot = useCallback(async (password) => {
    const r = await messageService.secureReset(password);
    if (!r.success) return { ok: false, error: r.error || 'THAT DIDN’T WORK' };
    applyStatus(r.data);
    // The old code is gone, and so are the passkeys: this device's face goes with them.
    await forgetBiometric({ userId });
    setHere(false);
    openModal({ kind: 'setup', type: 'pin', change: false, pending: { action: 'reset', threadId: modal?.pending?.threadId || null } });
    return { ok: true };
  }, [modal, userId, applyStatus, openModal]);

  const closeModal = useCallback(() => setModal(null), []);

  /** The API answered 423 for a thread: it's secure and the lock is shut. */
  const modalRef = useRef(modal);
  modalRef.current = modal;
  const handleLocked = useCallback((threadId) => {
    // A sheet is already up (the lock shut while a thread was loading): point it at this
    // thread rather than stacking a second one — and a second Face ID prompt — on top.
    if (modalRef.current?.kind === 'unlock') {
      setModal((m) => (m && m.kind === 'unlock' ? { ...m, pending: { ...(m.pending || {}), threadId } } : m));
      return;
    }
    messageService.getSecureLock().then((r) => {
      if (r.success) applyStatus(r.data);
      openVault({ threadId });
    });
  }, [applyStatus, openVault]);

  return {
    loaded,
    session,
    view,
    setView,
    unlocked,
    isOpenNow,
    msLeft,
    secureCount,
    secureUnread,
    windowMinutes: session.windowMinutes || DEFAULT_WINDOW_MIN,
    modal,
    toggleVault,
    openVault,
    leaveVault,
    lockNow,
    moveToSecure,
    removeFromSecure,
    changeLock,
    setWindow,
    handleLocked,
    bio,
    enableBiometric,
    disableBiometric,
    /** The one-time "use Face ID next time?" card: { label, accept, dismiss } or null. */
    offer: offer ? { label: bio.label, accept: acceptOffer, dismiss: dismissOffer } : null,
    sheet: { onSetup, onUnlock, onForgot, onClose: closeModal, biometric: bio.enrolled ? { label: bio.label, unlock: onBiometric, auto: bio.auto } : null },
  };
}
